import { TOOLS, ZOOM_START, LIMITS } from '@shared/protocol.js';
import {
  simplifyPoints, transformGeometry, geometryOf, preservesAspectByDefault,
} from '@shared/ops.js';
import { recogniseShape, squareUp } from '@shared/recognise.js';
import {
  floodMask, dilate, traceRings, simplifyRing,
} from '@shared/floodfill.js';
import { Scene } from './scene';
import {
  clampCamera, clampZoom, screenToWorld, visibleRect, zoomAt, cameraForBounds,
  cameraForPeerViewport, lerpCamera, pannableRegion, camerasClose, clamp, padBounds,
  worldToScreen as worldToScreenPoint,
} from './camera';
import {
  renderScene, drawBackground, drawPeerCursor, drawPeerViewport, drawLaser,
  drawShapePreview, drawPanFrontier, setImageLoadListener, resetTransform, drawOp,
  drawSelection,
} from './render';
import type {
  Bounds, Camera, LiveStroke, Op, Peer, ShapeOp, StampOp, StrokeOp, TextOp, ImageOp, FillOp,
  Viewport,
} from './types';
import type { PadConnection, ClearScope } from '@/lib/net';

export interface ToolState {
  tool: string;
  color: string;
  size: number;
  opacity: number;
  stamp: string;
  /** Which shape the shape tool draws. */
  shape: string;
  fillShapes: boolean;
  textSize: number;
}

export interface EngineSettings {
  theme: 'light' | 'dark';
  background: 'plain' | 'dots' | 'grid' | 'lines';
  showCursors: boolean;
  showCursorNames: boolean;
  showPeerViewports: boolean;
  pressure: boolean;
  smoothing: boolean;
  wheelZooms: boolean;
  showPanFrontier: boolean;
  showFps: boolean;
  /** Hold still at the end of a stroke to snap it to a clean shape. */
  snapShapes: boolean;
}

export interface EngineCallbacks {
  onCameraChange?: (cam: Camera, vp: Viewport) => void;
  onSceneChange?: () => void;
  /** Fired when the text tool is used, so the UI can show an input field. */
  onTextRequest?: (world: { x: number; y: number }, screen: { x: number; y: number }) => void;
  onHistoryChange?: (state: { canUndo: boolean; canRedo: boolean }) => void;
  /** Fired when the select tool picks something up or lets it go. */
  onSelectionChange?: (op: Op | null) => void;
  /** Fired when a held stroke snaps to a clean shape. */
  onSnap?: (shape: string) => void;
  onFps?: (fps: number) => void;
}

const LASER_LIFETIME = 1400;

/** How long the pointer must sit still before a stroke snaps to a shape. */
const SNAP_HOLD_MS = 650;
/** How far it may drift during that hold, in screen pixels. */
const SNAP_STILL_PX = 6;
/** Moving this far after a snap takes it back to freehand. */
const SNAP_ESCAPE_PX = 14;
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

/** Resize grips, plus `move` for dragging the body of a selection. */
export type HandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'move';

/** Half-size of a grip in screen pixels, and how far outside it still counts. */
const HANDLE_R = 7;
const HANDLE_HIT = 15;
/**
 * How different a pixel may be from the one clicked and still be flooded.
 * Generous enough to walk through the soft edge of an antialiased line, mean
 * enough not to bleed through a faint one.
 */
const FILL_TOLERANCE = 0.14;

/**
 * Undo entries. Moving something is not the same as creating or deleting it,
 * so the stack has to remember which of the three happened — and for a move,
 * the geometry to put back.
 */
type UndoEntry =
  | { kind: 'add'; id: string }
  | { kind: 'remove'; ids: string[] }
  | { kind: 'transform'; id: string; before: Record<string, unknown>; after: Record<string, unknown> };

/**
 * Owns everything that must not go through React: the render loop, the camera,
 * pointer handling and the wire. React only mounts it and pokes at it through
 * the small imperative surface below, which keeps 60fps drawing entirely free
 * of re-renders.
 */
export class PadEngine {
  readonly scene = new Scene();

  camera: Camera = { x: 0, y: 0, zoom: ZOOM_START };

  viewport: Viewport = { width: 1, height: 1, dpr: 1 };

  peers = new Map<string, Peer>();

  conn: PadConnection | null = null;

  /** Set true to draw every peer's viewport frame at once. */
  overview = false;

  /** Peer connection id we are mirroring, if any. */
  following: string | null = null;

  /**
   * When set, only ops up to this sequence number are painted. Drives the
   * timelapse replay without disturbing the real scene.
   */
  private replayLimit: number | null = null;

  private canvasMain: HTMLCanvasElement;

  private canvasOverlay: HTMLCanvasElement;

  private staticCanvas: HTMLCanvasElement;

  private ctxMain: CanvasRenderingContext2D;

  private ctxOverlay: CanvasRenderingContext2D;

  private ctxStatic: CanvasRenderingContext2D;

  private raf = 0;

  private staticDirty = true;

  private overlayDirty = true;

  private lastSceneVersion = -1;

  /** Strokes in flight, keyed by stroke id (mine and peers'). */
  private liveStrokes = new Map<string, LiveStroke>();

  private myStroke: { id: string; op: StrokeOp } | null = null;

  private pointers = new Map<number, { x: number; y: number }>();

  private gesture: { dist: number; cx: number; cy: number } | null = null;

  private panFrom: { sx: number; sy: number; cam: Camera } | null = null;

  private shapeDraft: ShapeOp | null = null;

  /** Shapes peers are still dragging out, keyed by their connection id. */
  private peerDrafts = new Map<string, Op>();

  /** Where the pointer last was in world space, for tools that act on release. */
  private lastPointerWorld: { x: number; y: number } | null = null;

  private laserPoints: number[] = [];

  private lastPenAt = 0;

  private spaceHeld = false;

  private shiftHeld = false;

  private animation: { from: Camera; to: Camera; start: number; duration: number } | null = null;

  /** Currently selected op id, if the select tool has hold of something. */
  private selectedId: string | null = null;

  /** In-flight move/resize: what we grabbed and where it started. */
  private transform: {
    opId: string;
    handle: HandleId;
    startBox: Bounds;
    beforeGeom: Record<string, unknown>;
    grabX: number;
    grabY: number;
    aspect: number;
  } | null = null;

  private lastTransformSent = 0;

  /**
   * Where the pointer settled, when, and how long the stroke was at that
   * moment. That last part matters: a resting hand still jitters, and those
   * points would otherwise be counted as part of the shape being drawn.
   */
  private stillAt: { x: number; y: number; since: number; at: number } | null = null;

  /** The clean shape a held stroke has snapped to, if any. */
  private snapped: ShapeOp | null = null;

  private undoStack: UndoEntry[] = [];

  private redoStack: UndoEntry[] = [];

  private frameTimes: number[] = [];

  private lastFpsReport = 0;

  private lastViewportSync = 0;

  private resizeObserver: ResizeObserver | null = null;

  private disposed = false;

  tools: ToolState = {
    tool: TOOLS.PEN,
    color: '#1a1859',
    size: 6,
    opacity: 1,
    stamp: '⭐',
    shape: 'rect',
    fillShapes: false,
    textSize: 28,
  };

  settings: EngineSettings = {
    theme: 'light',
    background: 'dots',
    showCursors: true,
    showCursorNames: true,
    showPeerViewports: true,
    pressure: true,
    smoothing: true,
    wheelZooms: false,
    showPanFrontier: true,
    showFps: false,
    snapShapes: true,
  };

  constructor(
    private container: HTMLElement,
    public me: { id: string; username: string; avatarColor: string },
    private callbacks: EngineCallbacks = {},
    public readonly: boolean = false,
  ) {
    this.canvasMain = this.makeCanvas(1);
    this.canvasOverlay = this.makeCanvas(2);
    this.staticCanvas = document.createElement('canvas');

    this.ctxMain = this.canvasMain.getContext('2d', { alpha: true })!;
    this.ctxOverlay = this.canvasOverlay.getContext('2d', { alpha: true })!;
    this.ctxStatic = this.staticCanvas.getContext('2d', { alpha: true })!;

    setImageLoadListener(() => this.invalidate());
    this.attachInput();
    this.observeSize();
    this.loop = this.loop.bind(this);
    this.raf = requestAnimationFrame(this.loop);
  }

  private makeCanvas(z: number) {
    const canvas = document.createElement('canvas');
    canvas.style.position = 'absolute';
    canvas.style.inset = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.zIndex = String(z);
    canvas.style.touchAction = 'none';
    if (z === 2) canvas.style.pointerEvents = 'auto';
    else canvas.style.pointerEvents = 'none';
    this.container.appendChild(canvas);
    return canvas;
  }

  // --- sizing -------------------------------------------------------------

  private observeSize() {
    const apply = () => {
      const rect = this.container.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
      if (rect.width < 1 || rect.height < 1) return;
      this.viewport = { width: rect.width, height: rect.height, dpr };
      for (const canvas of [this.canvasMain, this.canvasOverlay, this.staticCanvas]) {
        canvas.width = Math.round(rect.width * dpr);
        canvas.height = Math.round(rect.height * dpr);
      }
      this.setCamera(this.camera, { silent: false });
      this.staticDirty = true;
      this.overlayDirty = true;
    };
    apply();
    this.resizeObserver = new ResizeObserver(apply);
    this.resizeObserver.observe(this.container);
  }

  // --- render loop --------------------------------------------------------

  invalidate() {
    this.staticDirty = true;
    this.overlayDirty = true;
  }

  private loop(now: number) {
    if (this.disposed) return;
    this.raf = requestAnimationFrame(this.loop);

    if (this.animation) this.stepAnimation(now);
    if (this.following) this.stepFollow();

    // Heartbeat our viewport so a peer who is sitting perfectly still is still
    // findable in everyone else's overview.
    if (now - this.lastViewportSync > 2000) this.broadcastViewport(true);

    this.checkSnapHold(now);

    if (this.scene.version !== this.lastSceneVersion) {
      this.lastSceneVersion = this.scene.version;
      this.staticDirty = true;
      this.callbacks.onSceneChange?.();
    }

    if (this.staticDirty) {
      this.renderStatic();
      this.staticDirty = false;
    }

    // The main canvas is cheap to refresh: blit the cached artwork, then paint
    // whatever is still being drawn on top of it.
    this.renderMain();

    if (this.overlayDirty || this.hasTransientOverlay()) {
      this.renderOverlay(now);
      this.overlayDirty = false;
    }

    if (this.settings.showFps) this.trackFps(now);
  }

  private trackFps(now: number) {
    this.frameTimes.push(now);
    while (this.frameTimes.length && now - this.frameTimes[0] > 1000) this.frameTimes.shift();
    if (now - this.lastFpsReport > 500) {
      this.lastFpsReport = now;
      this.callbacks.onFps?.(this.frameTimes.length);
    }
  }

  private hasTransientOverlay() {
    if (this.transform) return true;
    if (this.snapped) return true;
    if (this.laserPoints.length) return true;
    if (this.shapeDraft) return true;
    if (this.peerDrafts.size) return true;
    for (const peer of this.peers.values()) {
      if (peer.laser && Date.now() - peer.laser.at < LASER_LIFETIME) return true;
      if (peer.cursor && this.settings.showCursors) return true;
    }
    return false;
  }

  private renderStatic() {
    const { ctxStatic: ctx, viewport: vp } = this;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.staticCanvas.width, this.staticCanvas.height);
    drawBackground(ctx, this.camera, vp, this.settings.background, this.settings.theme === 'dark');
    let ops = this.scene.query(visibleRect(this.camera, vp));
    if (this.replayLimit !== null) {
      const limit = this.replayLimit;
      ops = ops.filter((op) => (op.seq ?? Infinity) <= limit);
    }
    renderScene(ctx, vp, this.camera, ops, [], this.settings.pressure);
  }

  /** Show the pad as it looked at sequence `seq`; pass null to return to now. */
  setReplayLimit(seq: number | null) {
    if (this.replayLimit === seq) return;
    this.replayLimit = seq;
    this.staticDirty = true;
  }

  /** Sequence numbers of every live op, ascending — the replay timeline. */
  replayTimeline(): number[] {
    return this.scene.all()
      .map((op) => op.seq ?? 0)
      .filter((seq) => seq > 0)
      .sort((a, b) => a - b);
  }

  private renderMain() {
    const { ctxMain: ctx, viewport: vp } = this;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvasMain.width, this.canvasMain.height);
    ctx.drawImage(this.staticCanvas, 0, 0);

    if (this.liveStrokes.size) {
      renderScene(ctx, vp, this.camera, [], [...this.liveStrokes.values()], this.settings.pressure);
    }
  }

  private renderOverlay(now: number) {
    const { ctxOverlay: ctx, viewport: vp } = this;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvasOverlay.width, this.canvasOverlay.height);
    resetTransform(ctx, vp);

    if (this.shapeDraft) drawShapePreview(ctx, this.camera, vp, this.shapeDraft);
    if (this.snapped) drawShapePreview(ctx, this.camera, vp, this.snapped);
    for (const draft of this.peerDrafts.values()) {
      if (draft.kind === 'shape') drawShapePreview(ctx, this.camera, vp, draft);
    }

    const selected = this.selection;
    if (selected && this.tools.tool === TOOLS.SELECT) {
      drawSelection(
        ctx, this.camera, vp, this.scene.boundsOf(selected),
        HANDLE_R, this.settings.theme === 'dark',
      );
    }

    if (this.settings.showPanFrontier) {
      const region = pannableRegion(this.scene.bounds(), this.camera, vp);
      const rect = visibleRect(this.camera, vp);
      // Only worth showing once you are actually close to the edge.
      const nearEdge = rect.minX - region.minX < rect.maxX - rect.minX
        || region.maxX - rect.maxX < rect.maxX - rect.minX
        || rect.minY - region.minY < rect.maxY - rect.minY
        || region.maxY - rect.maxY < rect.maxY - rect.minY;
      if (nearEdge) drawPanFrontier(ctx, this.camera, vp, region, this.settings.theme === 'dark');
    }

    if (this.laserPoints.length) {
      drawLaser(ctx, this.camera, vp, this.laserPoints, this.tools.color, 0);
      resetTransform(ctx, vp);
    }

    for (const peer of this.peers.values()) {
      const color = peer.user.avatarColor;

      if (peer.laser) {
        const age = (now - peer.laser.at) / LASER_LIFETIME;
        if (age < 1) {
          drawLaser(ctx, this.camera, vp, peer.laser.points, color, age);
          resetTransform(ctx, vp);
        }
      }

      if ((this.overview || this.settings.showPeerViewports) && peer.viewport) {
        drawPeerViewport(ctx, this.camera, vp, peer.viewport, peer.user.username, color,
          !this.overview && this.following !== peer.connectionId);
      }

      if (this.settings.showCursors && peer.cursor) {
        drawPeerCursor(ctx, this.camera, vp, peer.cursor, peer.user.username, color,
          this.settings.showCursorNames);
      }
    }
  }

  // --- camera -------------------------------------------------------------

  setCamera(next: Camera, { silent = false, clampToContent = true } = {}) {
    const clamped = clampToContent
      ? clampCamera(next, this.viewport, this.scene.bounds())
      : { ...next, zoom: clampZoom(next.zoom) };
    const changed = clamped.x !== this.camera.x || clamped.y !== this.camera.y
      || clamped.zoom !== this.camera.zoom;
    this.camera = clamped;
    if (changed) {
      this.staticDirty = true;
      this.overlayDirty = true;
    }
    if (!silent && changed) {
      this.callbacks.onCameraChange?.(this.camera, this.viewport);
      this.broadcastViewport();
    }
    return this.camera;
  }

  private broadcastViewport(force = false) {
    this.lastViewportSync = performance.now();
    this.conn?.sendViewport({
      cx: this.camera.x,
      cy: this.camera.y,
      zoom: this.camera.zoom,
      w: this.viewport.width,
      h: this.viewport.height,
    }, force);
  }

  /**
   * Announce where we are looking even though nothing moved.
   *
   * Without this, someone who joins and sits still never appears in anyone
   * else's overview — there is no camera change to trigger a broadcast.
   */
  syncViewport() {
    this.broadcastViewport(true);
  }

  /** Animate to a camera. Cancels any follow. */
  flyTo(target: Camera, duration = 620) {
    this.following = null;
    const to = clampCamera(target, this.viewport, this.scene.bounds());
    if (camerasClose(this.camera, to, this.viewport)) return;
    this.animation = { from: { ...this.camera }, to, start: performance.now(), duration };
  }

  flyToBounds(box: Bounds | null, opts: { maxZoom?: number; duration?: number } = {}) {
    if (!box) return;
    const cam = cameraForBounds(box, this.viewport);
    if (opts.maxZoom) cam.zoom = Math.min(cam.zoom, opts.maxZoom);
    this.flyTo(cam, opts.duration);
  }

  private stepAnimation(now: number) {
    const anim = this.animation!;
    const t = (now - anim.start) / anim.duration;
    if (t >= 1) {
      this.animation = null;
      this.setCamera(anim.to);
      return;
    }
    this.setCamera(lerpCamera(anim.from, anim.to, t));
  }

  private stepFollow() {
    const peer = this.peers.get(this.following!);
    if (!peer?.viewport) return;
    const target = cameraForPeerViewport(peer.viewport, this.viewport);
    // Ease towards them rather than snapping, so their panning does not feel jerky.
    this.setCamera(lerpCamera(this.camera, target, 0.18), { clampToContent: false });
  }

  /** Jump to exactly what a peer is looking at. */
  goToPeer(connectionId: string, { follow = false } = {}) {
    const peer = this.peers.get(connectionId);
    if (!peer?.viewport) return false;
    this.flyTo(cameraForPeerViewport(peer.viewport, this.viewport));
    if (follow) {
      // Engage follow once the flight lands.
      window.setTimeout(() => { this.following = connectionId; }, 640);
    }
    return true;
  }

  stopFollowing() {
    this.following = null;
  }

  /** Frame every peer at once, for the "where is everybody" overview. */
  fitEveryone() {
    let box: Bounds | null = null;
    const add = (v: { cx: number; cy: number; zoom: number; w: number; h: number }) => {
      const halfW = v.w / (2 * v.zoom);
      const halfH = v.h / (2 * v.zoom);
      const b = { minX: v.cx - halfW, minY: v.cy - halfH, maxX: v.cx + halfW, maxY: v.cy + halfH };
      box = box ? {
        minX: Math.min(box.minX, b.minX),
        minY: Math.min(box.minY, b.minY),
        maxX: Math.max(box.maxX, b.maxX),
        maxY: Math.max(box.maxY, b.maxY),
      } : b;
    };
    add({
      cx: this.camera.x, cy: this.camera.y, zoom: this.camera.zoom, w: this.viewport.width, h: this.viewport.height,
    });
    for (const peer of this.peers.values()) if (peer.viewport) add(peer.viewport);
    if (box) this.flyTo(cameraForBounds(box, this.viewport, 0.18));
  }

  returnToStart() {
    this.flyTo({ x: 0, y: 0, zoom: 1 });
  }

  returnToMyWork() {
    const last = this.scene.lastOpBoundsForUser(this.me.id);
    if (last) {
      this.flyToBounds(padBounds(last, 1.8), { maxZoom: 3 });
      return true;
    }
    const mine = this.scene.boundsForUser(this.me.id);
    if (mine) {
      this.flyToBounds(padBounds(mine, 1.15), { maxZoom: 3 });
      return true;
    }
    this.returnToStart();
    return false;
  }

  fitEverything() {
    const box = this.scene.bounds();
    if (box) this.flyToBounds(padBounds(box, 1.06));
    else this.returnToStart();
  }

  zoomBy(factor: number) {
    this.animation = null;
    this.setCamera(zoomAt(this.camera, this.viewport, this.viewport.width / 2, this.viewport.height / 2, factor));
  }

  // --- configuration ------------------------------------------------------

  setTools(next: Partial<ToolState>) {
    this.tools = { ...this.tools, ...next };
  }

  setSettings(next: Partial<EngineSettings>) {
    const before = this.settings;
    this.settings = { ...before, ...next };
    if (before.background !== this.settings.background || before.theme !== this.settings.theme
      || before.pressure !== this.settings.pressure) {
      this.staticDirty = true;
    }
    this.overlayDirty = true;
  }

  setOverview(on: boolean) {
    this.overview = on;
    this.overlayDirty = true;
    if (on) this.fitEveryone();
  }

  // --- selection ----------------------------------------------------------

  /** The op the select tool currently has hold of. */
  get selection(): Op | null {
    return this.selectedId ? this.scene.get(this.selectedId) ?? null : null;
  }

  select(id: string | null) {
    if (this.selectedId === id) return;
    this.selectedId = id;
    this.overlayDirty = true;
    this.callbacks.onSelectionChange?.(this.selection);
  }

  /** Screen-space positions of the eight grips around a selection. */
  private handlePositions(box: Bounds) {
    const tl = worldToScreenPoint(this.camera, this.viewport, box.minX, box.minY);
    const br = worldToScreenPoint(this.camera, this.viewport, box.maxX, box.maxY);
    const mx = (tl.x + br.x) / 2;
    const my = (tl.y + br.y) / 2;
    return {
      nw: { x: tl.x, y: tl.y },
      n: { x: mx, y: tl.y },
      ne: { x: br.x, y: tl.y },
      e: { x: br.x, y: my },
      se: { x: br.x, y: br.y },
      s: { x: mx, y: br.y },
      sw: { x: tl.x, y: br.y },
      w: { x: tl.x, y: my },
    } as Record<Exclude<HandleId, 'move'>, { x: number; y: number }>;
  }

  /** Which grip (if any) is under a screen point. */
  private handleAt(sx: number, sy: number): HandleId | null {
    const op = this.selection;
    if (!op) return null;
    const positions = this.handlePositions(this.scene.boundsOf(op));
    for (const [id, p] of Object.entries(positions)) {
      if (Math.abs(sx - p.x) <= HANDLE_HIT && Math.abs(sy - p.y) <= HANDLE_HIT) return id as HandleId;
    }
    return null;
  }

  private beginTransform(handle: HandleId, world: { x: number; y: number }) {
    const op = this.selection;
    if (!op) return;
    const box = this.scene.boundsOf(op);
    this.transform = {
      opId: op.id,
      handle,
      startBox: { ...box },
      beforeGeom: geometryOf(op) as Record<string, unknown>,
      grabX: world.x,
      grabY: world.y,
      aspect: (box.maxX - box.minX) / Math.max(1e-6, box.maxY - box.minY),
    };
  }

  /**
   * Work out the new bounding box for a drag, then map the op's geometry onto
   * it. Corner grips keep proportions for photos, stamps and text (where
   * stretching looks like a mistake) and scale freely with Shift held; edge
   * grips always move a single axis.
   */
  private updateTransform(world: { x: number; y: number }) {
    const t = this.transform;
    if (!t) return;
    const op = this.scene.get(t.opId);
    if (!op) return;

    const dx = world.x - t.grabX;
    const dy = world.y - t.grabY;
    const box = { ...t.startBox };

    if (t.handle === 'move') {
      box.minX += dx;
      box.maxX += dx;
      box.minY += dy;
      box.maxY += dy;
    } else {
      if (t.handle.includes('w')) box.minX = t.startBox.minX + dx;
      if (t.handle.includes('e')) box.maxX = t.startBox.maxX + dx;
      if (t.handle.includes('n')) box.minY = t.startBox.minY + dy;
      if (t.handle.includes('s')) box.maxY = t.startBox.maxY + dy;

      const corner = t.handle.length === 2;
      const keepAspect = corner && (preservesAspectByDefault(op) ? !this.shiftHeld : this.shiftHeld);
      if (keepAspect) {
        const w = box.maxX - box.minX;
        const h = box.maxY - box.minY;
        // Follow whichever axis the pointer pushed further, so the drag feels
        // like it is tracking the finger rather than fighting it.
        if (Math.abs(w) / t.aspect > Math.abs(h)) {
          const nh = Math.sign(h || 1) * Math.abs(w) / t.aspect;
          if (t.handle.includes('n')) box.minY = box.maxY - nh;
          else box.maxY = box.minY + nh;
        } else {
          const nw = Math.sign(w || 1) * Math.abs(h) * t.aspect;
          if (t.handle.includes('w')) box.minX = box.maxX - nw;
          else box.maxX = box.minX + nw;
        }
      }

      // Never let a box collapse through zero — the geometry would invert and
      // the next drag would have nothing to scale from.
      const minSize = 2 / this.camera.zoom;
      if (Math.abs(box.maxX - box.minX) < minSize) {
        if (t.handle.includes('w')) box.minX = box.maxX - minSize;
        else box.maxX = box.minX + minSize;
      }
      if (Math.abs(box.maxY - box.minY) < minSize) {
        if (t.handle.includes('n')) box.minY = box.maxY - minSize;
        else box.maxY = box.minY + minSize;
      }
    }

    // Map from the geometry as it was when the drag started, not as it is now.
    // Feeding the already-moved op back in would compound every frame, so the
    // thing would accelerate away from the pointer.
    const patch = transformGeometry({ ...op, ...t.beforeGeom } as Op, t.startBox, box);
    Object.assign(op, patch);
    this.scene.refresh(op.id);

    // Stream the move so peers see it happen, but only at ~20Hz and without
    // touching the database until the drag ends.
    const now = performance.now();
    if (now - this.lastTransformSent > 50) {
      this.lastTransformSent = now;
      this.conn?.updateOp(op.id, patch, true);
    }
    this.overlayDirty = true;
  }

  private endTransform() {
    const t = this.transform;
    this.transform = null;
    if (!t) return;
    const op = this.scene.get(t.opId);
    if (!op) return;

    const after = geometryOf(op) as Record<string, unknown>;
    // A tap that moved nothing should not fill the undo stack.
    if (JSON.stringify(after) === JSON.stringify(t.beforeGeom)) return;

    this.conn?.updateOp(op.id, after, false);
    this.undoStack.push({ kind: 'transform', id: op.id, before: t.beforeGeom, after });
    this.redoStack = [];
    this.emitHistory();
  }

  /** Apply a geometry change that arrived from someone else. */
  applyRemoteUpdate(op: Op) {
    const existing = this.scene.get(op.id);
    if (!existing) {
      this.scene.put(op);
      return;
    }
    // Do not yank something out from under our own hands mid-drag.
    if (this.transform?.opId === op.id) return;
    Object.assign(existing, op);
    this.scene.refresh(op.id);
    if (this.selectedId === op.id) this.overlayDirty = true;
  }

  /** Delete whatever is selected. */
  deleteSelection() {
    const op = this.selection;
    if (!op) return false;
    this.scene.setDeleted([op.id], true);
    this.conn?.deleteOps([op.id]);
    this.undoStack.push({ kind: 'remove', ids: [op.id] });
    this.redoStack = [];
    this.select(null);
    this.emitHistory();
    return true;
  }

  /** Frame the selection, for "where did that go". */
  scrollToSelection() {
    const op = this.selection;
    if (op) this.flyToBounds(padBounds(this.scene.boundsOf(op), 2.2), { maxZoom: 3 });
  }

  // --- op plumbing --------------------------------------------------------

  private pushLocalOp(op: Op, { record = true } = {}) {
    // Until the server assigns a sequence number the op paints on top, which is
    // what the author expects — their own mark appears instantly.
    this.scene.put({ ...op, userId: this.me.id, seq: Number.MAX_SAFE_INTEGER } as Op);
    if (record) {
      this.undoStack.push({ kind: 'add', id: op.id });
      this.redoStack = [];
      this.emitHistory();
    }
  }

  private emitHistory() {
    this.callbacks.onHistoryChange?.({
      canUndo: this.undoStack.length > 0,
      canRedo: this.redoStack.length > 0,
    });
  }

  /** Apply an op that arrived from the network. */
  applyRemoteOp(op: Op) {
    this.scene.put(op);
  }

  applyDelete(ids: string[]) {
    this.scene.setDeleted(ids, true);
  }

  applyRestore(ids: string[]) {
    this.scene.setDeleted(ids, false);
  }

  /** Put an op's geometry back to a remembered state. */
  private applyGeometry(id: string, geom: Record<string, unknown>) {
    const op = this.scene.get(id);
    if (!op) return false;
    Object.assign(op, geom);
    this.scene.refresh(id);
    this.conn?.updateOp(id, geom, false);
    if (this.selectedId === id) this.overlayDirty = true;
    return true;
  }

  undo() {
    while (this.undoStack.length) {
      const entry = this.undoStack.pop()!;

      if (entry.kind === 'add') {
        const op = this.scene.get(entry.id);
        if (!op || op.deleted) continue;
        this.scene.setDeleted([entry.id], true);
        this.conn?.deleteOps([entry.id]);
        if (this.selectedId === entry.id) this.select(null);
      } else if (entry.kind === 'remove') {
        const live = entry.ids.filter((id) => this.scene.get(id));
        if (!live.length) continue;
        this.scene.setDeleted(live, false);
        this.conn?.restoreOps(live);
      } else if (!this.applyGeometry(entry.id, entry.before)) {
        continue;
      }

      this.redoStack.push(entry);
      this.emitHistory();
      return true;
    }
    this.emitHistory();
    return false;
  }

  redo() {
    while (this.redoStack.length) {
      const entry = this.redoStack.pop()!;

      if (entry.kind === 'add') {
        if (!this.scene.get(entry.id)) continue;
        this.scene.setDeleted([entry.id], false);
        this.conn?.restoreOps([entry.id]);
      } else if (entry.kind === 'remove') {
        const live = entry.ids.filter((id) => this.scene.get(id));
        if (!live.length) continue;
        this.scene.setDeleted(live, true);
        this.conn?.deleteOps(live);
        if (this.selectedId && live.includes(this.selectedId)) this.select(null);
      } else if (!this.applyGeometry(entry.id, entry.after)) {
        continue;
      }

      this.undoStack.push(entry);
      this.emitHistory();
      return true;
    }
    this.emitHistory();
    return false;
  }

  clear(scope: ClearScope) {
    this.conn?.clear(scope);
    const mine = this.scene.opsByUser(this.me.id).filter((op) => !op.deleted);
    const ids = scope === 'all'
      ? this.scene.all().map((op) => op.id)
      : scope === 'mine-images'
        ? mine.filter((op) => op.kind === 'image').map((op) => op.id)
        : scope === 'mine-drawing'
          ? mine.filter((op) => op.kind !== 'image').map((op) => op.id)
          : mine.map((op) => op.id);

    this.scene.setDeleted(ids, true);
    this.select(null);
    // A clear is undoable as one step, rather than wiping the history.
    this.undoStack.push({ kind: 'remove', ids });
    this.redoStack = [];
    this.emitHistory();
  }

  // --- live strokes from peers -------------------------------------------

  peerStrokeBegin(strokeId: string, style: LiveStroke['style'], points: number[], userId: string, connectionId: string) {
    this.liveStrokes.set(strokeId, { strokeId, connectionId, userId, style, points: [...points] });
  }

  peerStrokePoints(strokeId: string, points: number[]) {
    const live = this.liveStrokes.get(strokeId);
    if (live) live.points.push(...points);
  }

  peerStrokeEnd(strokeId: string, op: Op | null) {
    this.liveStrokes.delete(strokeId);
    if (op) this.scene.put(op);
  }

  /** A shape a peer is still dragging out; null once they let go. */
  peerDraft(connectionId: string, op: Op | null) {
    if (op) this.peerDrafts.set(connectionId, op);
    else this.peerDrafts.delete(connectionId);
    this.overlayDirty = true;
  }

  peerLaser(connectionId: string, points: number[]) {
    const peer = this.peers.get(connectionId);
    if (peer) {
      peer.laser = { points, at: Date.now() };
      this.overlayDirty = true;
    }
  }

  // --- input --------------------------------------------------------------

  private attachInput() {
    const el = this.canvasOverlay;
    el.addEventListener('pointerdown', this.onPointerDown);
    el.addEventListener('pointermove', this.onPointerMove);
    el.addEventListener('pointerup', this.onPointerUp);
    el.addEventListener('pointercancel', this.onPointerUp);
    el.addEventListener('pointerleave', this.onPointerLeave);
    el.addEventListener('wheel', this.onWheel, { passive: false });
    el.addEventListener('contextmenu', this.onContextMenu);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
  }

  private detachInput() {
    const el = this.canvasOverlay;
    el.removeEventListener('pointerdown', this.onPointerDown);
    el.removeEventListener('pointermove', this.onPointerMove);
    el.removeEventListener('pointerup', this.onPointerUp);
    el.removeEventListener('pointercancel', this.onPointerUp);
    el.removeEventListener('pointerleave', this.onPointerLeave);
    el.removeEventListener('wheel', this.onWheel);
    el.removeEventListener('contextmenu', this.onContextMenu);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
  }

  private onContextMenu = (e: Event) => e.preventDefault();

  private localPoint(e: PointerEvent | WheelEvent) {
    const rect = this.canvasOverlay.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  private pressureOf(e: PointerEvent) {
    if (!this.settings.pressure) return 0.5;
    if (e.pointerType === 'pen') return e.pressure > 0 ? e.pressure : 0.5;
    if (e.pointerType === 'touch') return e.pressure > 0 && e.pressure !== 1 ? e.pressure : 0.5;
    return 0.5;
  }

  /** True when this event should be ignored as palm contact next to a stylus. */
  private isPalm(e: PointerEvent) {
    return e.pointerType === 'touch' && performance.now() - this.lastPenAt < 1200;
  }

  private wantsPan(e: PointerEvent) {
    return this.tools.tool === TOOLS.PAN
      || this.spaceHeld
      || e.button === 1
      || (e.pointerType === 'mouse' && e.button === 2);
  }

  private onPointerDown = (e: PointerEvent) => {
    if (this.isPalm(e)) return;
    if (e.pointerType === 'pen') this.lastPenAt = performance.now();

    this.canvasOverlay.setPointerCapture(e.pointerId);
    const p = this.localPoint(e);
    this.pointers.set(e.pointerId, p);
    this.animation = null;
    this.following = null;

    // Second finger down: abandon the stroke and switch to pinch-zoom.
    if (this.pointers.size === 2) {
      this.abortStroke();
      this.startGesture();
      return;
    }
    if (this.pointers.size > 2) return;

    if (this.wantsPan(e)) {
      this.panFrom = { sx: p.x, sy: p.y, cam: { ...this.camera } };
      return;
    }
    if (this.readonly) return;

    const world = screenToWorld(this.camera, this.viewport, p.x, p.y);
    this.lastPointerWorld = world;

    // Announce the press itself. Without this a peer who taps without moving —
    // dotting an i, placing a stamp — is invisible until they drag, because
    // only pointermove used to broadcast.
    this.conn?.sendCursor({ x: world.x, y: world.y, down: true, tool: this.tools.tool }, true);

    if (this.tools.tool === TOOLS.SELECT) {
      // A grip on the current selection wins over whatever is underneath it.
      const handle = this.handleAt(p.x, p.y);
      if (handle) {
        this.beginTransform(handle, world);
        return;
      }
      const hit = this.scene.hitTest(world.x, world.y, 6 / this.camera.zoom);
      this.select(hit ? hit.id : null);
      if (hit) this.beginTransform('move', world);
      return;
    }

    this.beginToolAction(world, this.pressureOf(e));
  };

  private onPointerMove = (e: PointerEvent) => {
    if (this.isPalm(e)) return;
    if (e.pointerType === 'pen') this.lastPenAt = performance.now();

    const p = this.localPoint(e);
    const tracked = this.pointers.has(e.pointerId);
    if (tracked) this.pointers.set(e.pointerId, p);

    if (this.pointers.size >= 2) {
      this.updateGesture();
      return;
    }

    if (this.panFrom) {
      const dx = (p.x - this.panFrom.sx) / this.camera.zoom;
      const dy = (p.y - this.panFrom.sy) / this.camera.zoom;
      this.setCamera({ ...this.panFrom.cam, x: this.panFrom.cam.x - dx, y: this.panFrom.cam.y - dy });
      return;
    }

    const world = screenToWorld(this.camera, this.viewport, p.x, p.y);

    // Broadcast the pointer even when not drawing, so peers see you moving.
    if (!this.readonly) {
      this.conn?.sendCursor({ x: world.x, y: world.y, down: tracked, tool: this.tools.tool });
    }

    if (this.transform) {
      this.updateTransform(world);
      return;
    }

    // Hovering a grip should say so before you press.
    if (this.tools.tool === TOOLS.SELECT && !tracked) {
      const handle = this.handleAt(p.x, p.y);
      this.canvasOverlay.style.cursor = handle ? cursorForHandle(handle) : '';
    }

    if (!tracked) return;

    // Coalesced events give every sample the browser captured between frames,
    // which is the difference between a smooth curve and a polygon.
    const samples = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [];
    if (samples.length > 1) {
      for (const sample of samples) {
        const sp = this.localPoint(sample);
        this.continueToolAction(screenToWorld(this.camera, this.viewport, sp.x, sp.y), this.pressureOf(sample));
      }
    } else {
      this.continueToolAction(world, this.pressureOf(e));
    }
  };

  private onPointerUp = (e: PointerEvent) => {
    this.pointers.delete(e.pointerId);
    try { this.canvasOverlay.releasePointerCapture(e.pointerId); } catch { /* not captured */ }

    if (this.pointers.size < 2) this.gesture = null;
    if (this.pointers.size === 1) {
      // Lifting one finger of a pinch: re-anchor the pan to the remaining one.
      const [remaining] = [...this.pointers.values()];
      this.panFrom = { sx: remaining.x, sy: remaining.y, cam: { ...this.camera } };
      return;
    }
    if (this.pointers.size > 0) return;

    this.panFrom = null;
    if (this.transform) this.endTransform();
    else this.endToolAction();
    if (!this.readonly) this.conn?.sendCursor(null);
  };

  private onPointerLeave = () => {
    if (!this.pointers.size) this.conn?.sendCursor(null);
  };

  private startGesture() {
    const [a, b] = [...this.pointers.values()];
    this.panFrom = null;
    this.gesture = {
      dist: Math.hypot(a.x - b.x, a.y - b.y),
      cx: (a.x + b.x) / 2,
      cy: (a.y + b.y) / 2,
    };
  }

  private updateGesture() {
    if (!this.gesture) {
      this.startGesture();
      return;
    }
    const [a, b] = [...this.pointers.values()];
    if (!a || !b) return;
    const dist = Math.hypot(a.x - b.x, a.y - b.y);
    const cx = (a.x + b.x) / 2;
    const cy = (a.y + b.y) / 2;

    // Pan by however far the midpoint moved, then zoom around the new midpoint.
    let cam = {
      ...this.camera,
      x: this.camera.x - (cx - this.gesture.cx) / this.camera.zoom,
      y: this.camera.y - (cy - this.gesture.cy) / this.camera.zoom,
    };
    if (this.gesture.dist > 4 && dist > 4) {
      cam = zoomAt(cam, this.viewport, cx, cy, dist / this.gesture.dist);
    }
    this.gesture = { dist, cx, cy };
    this.setCamera(cam);
  }

  private onWheel = (e: WheelEvent) => {
    e.preventDefault();
    this.animation = null;
    this.following = null;
    const p = this.localPoint(e);

    // A trackpad pinch arrives as a wheel event with ctrlKey set.
    const shouldZoom = e.ctrlKey || e.metaKey || this.settings.wheelZooms;
    if (shouldZoom) {
      const factor = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0022));
      this.setCamera(zoomAt(this.camera, this.viewport, p.x, p.y, factor));
      return;
    }
    const dx = (e.shiftKey ? e.deltaY : e.deltaX) / this.camera.zoom;
    const dy = (e.shiftKey ? 0 : e.deltaY) / this.camera.zoom;
    this.setCamera({ ...this.camera, x: this.camera.x + dx, y: this.camera.y + dy });
  };

  private onKeyDown = (e: KeyboardEvent) => {
    this.shiftHeld = e.shiftKey;
    if (e.code === 'Space' && !isTypingTarget(e.target)) {
      this.spaceHeld = true;
      this.canvasOverlay.style.cursor = 'grab';
    }
  };

  private onKeyUp = (e: KeyboardEvent) => {
    this.shiftHeld = e.shiftKey;
    if (e.code === 'Space') {
      this.spaceHeld = false;
      this.canvasOverlay.style.cursor = '';
    }
  };

  // --- tool actions -------------------------------------------------------

  /**
   * Brush sizes are chosen in screen pixels, so convert to world units at the
   * current zoom. A 6px pen stays a 6px pen whether you are zoomed right in on
   * a detail or looking at the whole drawing — zooming in buys you precision,
   * not a thicker line.
   */
  private worldSize(screenPx: number) {
    return Math.max(0.02, screenPx / this.camera.zoom);
  }

  private beginToolAction(world: { x: number; y: number }, pressure: number) {
    const { tool } = this.tools;

    switch (tool) {
      case TOOLS.STAMP: {
        const op: StampOp = {
          id: uid(),
          kind: 'stamp',
          char: this.tools.stamp,
          x: world.x,
          y: world.y,
          size: this.worldSize(Math.max(24, this.tools.size * 6)),
          rotation: 0,
        };
        this.pushLocalOp(op);
        this.conn?.commitOp(op);
        break;
      }

      case TOOLS.TEXT:
        // Deliberately does nothing here. Mounting the input while the pointer
        // is still down means the browser's own mousedown handling moves focus
        // straight back out of it, and the field vanishes before you can type
        // — which is exactly what happened on desktop, where that focus move
        // exists. It opens on release instead; see endToolAction.
        break;

      case TOOLS.FILL: {
        this.floodFill(world);
        break;
      }

      case TOOLS.OBJECT_ERASER: {
        this.eraseObjectAt(world);
        break;
      }

      case TOOLS.SHAPE: {
        this.shapeDraft = {
          id: uid(),
          kind: 'shape',
          shape: this.tools.shape,
          color: this.tools.color,
          fill: this.tools.fillShapes ? this.tools.color : null,
          size: this.worldSize(this.tools.size),
          opacity: this.tools.opacity,
          x0: world.x,
          y0: world.y,
          x1: world.x,
          y1: world.y,
        };
        this.overlayDirty = true;
        break;
      }

      case TOOLS.LASER: {
        this.laserPoints = [world.x, world.y, 0.5];
        this.overlayDirty = true;
        break;
      }

      default:
        this.startStroke(world, pressure);
    }
  }

  private continueToolAction(world: { x: number; y: number }, pressure: number) {
    this.lastPointerWorld = world;
    if (this.shapeDraft) {
      if (this.shiftHeld) {
        // Constrain to a square box, so circles stay circular.
        const dx = world.x - this.shapeDraft.x0;
        const dy = world.y - this.shapeDraft.y0;
        const side = Math.max(Math.abs(dx), Math.abs(dy));
        this.shapeDraft.x1 = this.shapeDraft.x0 + Math.sign(dx || 1) * side;
        this.shapeDraft.y1 = this.shapeDraft.y0 + Math.sign(dy || 1) * side;
      } else {
        this.shapeDraft.x1 = world.x;
        this.shapeDraft.y1 = world.y;
      }
      this.overlayDirty = true;
      // Stream the draft so peers watch the shape being sized, rather than
      // having it appear finished. Coalesced to one message per frame.
      this.conn?.sendDraft(this.shapeDraft);
      return;
    }
    if (this.tools.tool === TOOLS.LASER) {
      this.laserPoints.push(world.x, world.y, 0.5);
      if (this.laserPoints.length > 300) this.laserPoints.splice(0, 3);
      this.conn?.sendLaser(this.laserPoints.slice(-180));
      this.overlayDirty = true;
      return;
    }
    if (this.tools.tool === TOOLS.OBJECT_ERASER) {
      this.eraseObjectAt(world);
      return;
    }
    this.extendStroke(world, pressure);
  }

  private endToolAction() {
    if (this.shapeDraft) {
      const draft = this.shapeDraft;
      this.shapeDraft = null;
      this.overlayDirty = true;
      this.conn?.sendDraft(null);
      // Ignore an accidental click that produced no shape at all.
      if (Math.hypot(draft.x1 - draft.x0, draft.y1 - draft.y0) * this.camera.zoom > 3) {
        this.pushLocalOp(draft);
        this.conn?.commitOp(draft);
      }
      return;
    }
    if (this.tools.tool === TOOLS.LASER) {
      this.laserPoints = [];
      this.overlayDirty = true;
      return;
    }
    if (this.tools.tool === TOOLS.TEXT) {
      // Opened on release, not on press: see beginToolAction.
      const world = this.lastPointerWorld;
      if (world) {
        const screen = worldToScreenPoint(this.camera, this.viewport, world.x, world.y);
        this.callbacks.onTextRequest?.(world, screen);
      }
      return;
    }
    if (this.tools.tool === TOOLS.FILL) return;
    this.finishStroke();
  }

  /**
   * Hold-to-snap. While a freehand stroke is in progress, watch for the pointer
   * settling; once it has been still long enough, see whether the stroke is
   * recognisably a line, box, circle or triangle and swap it for the clean
   * version. Moving again puts the freehand stroke back.
   */
  private checkSnapHold(now: number) {
    if (!this.settings.snapShapes || !this.myStroke) return;
    if (!this.stillAt) return;
    if (this.snapped) return;
    if (now - this.stillAt.since < SNAP_HOLD_MS) return;

    // Recognise the stroke as it stood when the pointer came to rest. The
    // jitter a resting hand adds afterwards would otherwise pad out the path
    // length and make a perfectly straight line look like a wobble.
    const drawn = this.myStroke.op.points.slice(0, Math.max(6, this.stillAt.at));
    // Work in screen pixels so the thresholds mean the same at every zoom.
    const found = recogniseShape(drawn, { minSize: 14 / this.camera.zoom });
    // Mark the hold as spent either way, so a stroke that is not a shape does
    // not re-run recognition every frame while the finger rests.
    this.stillAt.since = Infinity;
    if (!found) return;

    const box = squareUp(found);
    this.snapped = {
      id: uid(),
      kind: 'shape',
      shape: box.shape,
      color: this.myStroke.op.color,
      fill: this.tools.fillShapes ? this.myStroke.op.color : null,
      size: this.myStroke.op.size,
      opacity: this.myStroke.op.opacity,
      x0: box.x0,
      y0: box.y0,
      x1: box.x1,
      y1: box.y1,
    };

    // Hide the freehand version; the overlay shows the snapped shape instead.
    this.liveStrokes.delete(this.myStroke.id);
    this.overlayDirty = true;
    this.callbacks.onSnap?.(box.shape);
  }

  private startStroke(world: { x: number; y: number }, pressure: number) {
    const id = uid();
    const op: StrokeOp = {
      id,
      kind: 'stroke',
      tool: this.tools.tool,
      color: this.tools.color,
      size: this.worldSize(this.tools.size),
      opacity: this.tools.opacity,
      smooth: this.settings.smoothing,
      points: [world.x, world.y, pressure],
    };
    this.myStroke = { id, op };
    this.stillAt = null;
    this.snapped = null;
    const style = {
      tool: op.tool, color: op.color, size: op.size, opacity: op.opacity, smooth: op.smooth,
    };
    this.liveStrokes.set(id, {
      strokeId: id, connectionId: 'me', userId: this.me.id, style, points: op.points,
    });
    this.conn?.beginStroke(id, style, op.points);
  }

  private extendStroke(world: { x: number; y: number }, pressure: number) {
    const stroke = this.myStroke;
    if (!stroke) return;
    const pts = stroke.op.points;
    const n = pts.length;

    if (this.settings.snapShapes) this.trackStillness(world);

    // Drop samples that land on top of the previous one; they add cost and
    // nothing else. The threshold is in screen pixels so it adapts to zoom.
    const minStep = 0.7 / this.camera.zoom;
    if (n >= 3) {
      const dx = world.x - pts[n - 3];
      const dy = world.y - pts[n - 2];
      if (dx * dx + dy * dy < minStep * minStep) return;
    }
    if (n / 3 >= LIMITS.maxPointsPerStroke) return;

    pts.push(world.x, world.y, pressure);
    this.conn?.pushPoints(stroke.id, [world.x, world.y, pressure]);
  }

  /** Note whether the pointer is resting, and undo a snap if it moves off. */
  private trackStillness(world: { x: number; y: number }) {
    const now = performance.now();
    const still = SNAP_STILL_PX / this.camera.zoom;
    const escape = SNAP_ESCAPE_PX / this.camera.zoom;

    if (!this.stillAt) {
      this.stillAt = {
        x: world.x, y: world.y, since: now, at: this.myStroke?.op.points.length ?? 0,
      };
      return;
    }

    const moved = Math.hypot(world.x - this.stillAt.x, world.y - this.stillAt.y);

    if (this.snapped) {
      // Carry on drawing and the snap is abandoned — the freehand stroke you
      // actually made comes back.
      if (moved > escape) {
        this.snapped = null;
        if (this.myStroke) {
          this.liveStrokes.set(this.myStroke.id, {
            strokeId: this.myStroke.id,
            connectionId: 'me',
            userId: this.me.id,
            style: {
              tool: this.myStroke.op.tool,
              color: this.myStroke.op.color,
              size: this.myStroke.op.size,
              opacity: this.myStroke.op.opacity,
              smooth: this.myStroke.op.smooth,
            },
            points: this.myStroke.op.points,
          });
        }
        this.stillAt = {
          x: world.x, y: world.y, since: now, at: this.myStroke?.op.points.length ?? 0,
        };
        this.overlayDirty = true;
      }
      return;
    }

    if (moved > still) {
      this.stillAt = {
        x: world.x, y: world.y, since: now, at: this.myStroke?.op.points.length ?? 0,
      };
    }
  }

  private finishStroke() {
    const stroke = this.myStroke;
    if (!stroke) return;
    this.myStroke = null;
    this.liveStrokes.delete(stroke.id);
    this.stillAt = null;

    // A stroke that snapped commits as the clean shape instead.
    const snapped = this.snapped;
    this.snapped = null;
    if (snapped) {
      this.overlayDirty = true;
      // Tell peers to drop the freehand line they watched being drawn.
      this.conn?.endStroke(stroke.id, null);
      this.pushLocalOp(snapped);
      this.conn?.commitOp(snapped);
      return;
    }

    const op: StrokeOp = {
      ...stroke.op,
      points: simplifyPoints(stroke.op.points, 0.4 / this.camera.zoom) as number[],
    };
    this.pushLocalOp(op);
    this.conn?.endStroke(stroke.id, op);
  }

  /** Discard the stroke in progress, e.g. when a pinch starts. */
  private abortStroke() {
    if (this.transform) {
      // Roll back to where the drag started, rather than leaving it half moved.
      this.applyGeometry(this.transform.opId, this.transform.beforeGeom);
      this.transform = null;
    }
    if (this.shapeDraft) {
      this.shapeDraft = null;
      this.overlayDirty = true;
    }
    const stroke = this.myStroke;
    if (!stroke) return;
    this.myStroke = null;
    this.snapped = null;
    this.stillAt = null;
    this.liveStrokes.delete(stroke.id);
    this.conn?.endStroke(stroke.id, null);
  }

  /**
   * Paint-bucket. Flood the region under the pointer against what is currently
   * on screen, then store the outline that produced rather than the pixels —
   * see shared/floodfill.js for why.
   *
   * Bounded to the viewport: an unbounded canvas has no edge for a fill to stop
   * at, so "as far as you can see" is the only honest limit. If the shape has a
   * gap the fill escapes and floods the background, which is what the user
   * asked for and what every paint program does.
   */
  private floodFill(world: { x: number; y: number }) {
    const { viewport: vp } = this;
    const dpr = this.staticCanvas.width / Math.max(1, vp.width);

    // Work at no more than ~1.2M pixels: a 4K window at dpr 2 would otherwise
    // be 35M and take seconds. Downscaling costs a little edge precision and
    // nothing else, because the result is traced back into world coordinates.
    const budget = 1_200_000;
    const full = this.staticCanvas.width * this.staticCanvas.height;
    const shrink = full > budget ? Math.sqrt(budget / full) : 1;
    const w = Math.max(1, Math.round(this.staticCanvas.width * shrink));
    const h = Math.max(1, Math.round(this.staticCanvas.height * shrink));

    const work = document.createElement('canvas');
    work.width = w;
    work.height = h;
    const wctx = work.getContext('2d', { willReadFrequently: true });
    if (!wctx) return;
    wctx.drawImage(this.staticCanvas, 0, 0, w, h);

    const screen = worldToScreenPoint(this.camera, vp, world.x, world.y);
    const px = screen.x * dpr * shrink;
    const py = screen.y * dpr * shrink;

    let image: ImageData;
    try {
      image = wctx.getImageData(0, 0, w, h);
    } catch {
      return;                                   // tainted canvas; nothing we can do
    }

    const { mask, count } = floodMask(
      { data: image.data, width: w, height: h }, px, py, FILL_TOLERANCE,
    );
    if (!count) return;

    // Grow slightly so the fill slides under the antialiased edge of whatever
    // bounds it, instead of leaving a pale seam.
    const grown = dilate(mask, w, h, 2);
    const rings = traceRings(grown, w, h, LIMITS.maxFillRings);
    if (!rings.length) return;

    // Back to world space. The mask is in downscaled device pixels; undo the
    // shrink, the device-pixel ratio and the camera in one go.
    const scale = 1 / (dpr * shrink);
    const originX = this.camera.x - (vp.width / 2) / this.camera.zoom;
    const originY = this.camera.y - (vp.height / 2) / this.camera.zoom;
    const toWorldX = (mx: number) => originX + (mx * scale) / this.camera.zoom;
    const toWorldY = (my: number) => originY + (my * scale) / this.camera.zoom;

    const worldRings: number[][] = [];
    let budgetLeft = LIMITS.maxFillPoints;
    for (const ring of rings) {
      const simple = simplifyRing(ring);
      if (simple.length < 6) continue;
      if (simple.length / 2 > budgetLeft) break;
      budgetLeft -= simple.length / 2;
      const out = new Array(simple.length);
      for (let i = 0; i < simple.length; i += 2) {
        out[i] = toWorldX(simple[i]);
        out[i + 1] = toWorldY(simple[i + 1]);
      }
      worldRings.push(out);
    }
    if (!worldRings.length) return;

    const op: FillOp = {
      id: uid(),
      kind: 'fill',
      rings: worldRings,
      color: this.tools.color,
      opacity: this.tools.opacity,
    };
    this.pushLocalOp(op);
    this.conn?.commitOp(op);
  }

  private eraseObjectAt(world: { x: number; y: number }) {
    const radius = this.worldSize(Math.max(this.tools.size, 8));
    const hit = this.scene.hitTest(world.x, world.y, radius);
    if (!hit) return;
    this.scene.setDeleted([hit.id], true);
    this.conn?.deleteOps([hit.id]);
    this.undoStack.push({ kind: 'remove', ids: [hit.id] });
    this.redoStack = [];
    this.emitHistory();
  }

  // --- content insertion --------------------------------------------------

  commitText(world: { x: number; y: number }, text: string) {
    const trimmed = text.trim();
    if (!trimmed) return;
    const op: TextOp = {
      id: uid(),
      kind: 'text',
      text: trimmed.slice(0, LIMITS.maxTextLength),
      x: world.x,
      y: world.y,
      size: this.tools.textSize / this.camera.zoom,
      color: this.tools.color,
      font: 'sans',
    };
    this.pushLocalOp(op);
    this.conn?.commitOp(op);
  }

  /** Drop an image into the middle of the view, scaled to fit comfortably. */
  placeImage(src: string, naturalW: number, naturalH: number) {
    const maxW = (this.viewport.width * 0.6) / this.camera.zoom;
    const maxH = (this.viewport.height * 0.6) / this.camera.zoom;
    const scale = Math.min(maxW / naturalW, maxH / naturalH, 1e6);
    const w = naturalW * scale;
    const h = naturalH * scale;
    const op: ImageOp = {
      id: uid(),
      kind: 'image',
      src,
      x: this.camera.x - w / 2,
      y: this.camera.y - h / 2,
      w,
      h,
      opacity: 1,
    };
    this.pushLocalOp(op);
    this.conn?.commitOp(op);
    return op;
  }

  // --- export -------------------------------------------------------------

  /**
   * Render the whole drawing to a PNG data URL. Used both for the library
   * thumbnail and for the share/export action.
   */
  exportPNG({ maxSize = 2048, padding = 24, background }: {
    maxSize?: number; padding?: number; background?: string | null;
  } = {}): string | null {
    const box = this.scene.bounds();
    if (!box) return null;

    const w = Math.max(1, box.maxX - box.minX);
    const h = Math.max(1, box.maxY - box.minY);
    const scale = Math.min(maxSize / w, maxSize / h, 4);
    const outW = Math.max(1, Math.round(w * scale + padding * 2));
    const outH = Math.max(1, Math.round(h * scale + padding * 2));

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const bg = background === undefined
      ? (this.settings.theme === 'dark' ? '#17181c' : '#ffffff')
      : background;
    if (bg) {
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, outW, outH);
    }

    ctx.translate(padding, padding);
    ctx.scale(scale, scale);
    ctx.translate(-box.minX, -box.minY);
    for (const op of this.scene.all()) drawOp(ctx, op, this.settings.pressure);

    return canvas.toDataURL('image/png');
  }

  /** Small preview for the library list. */
  exportThumbnail(size = 480) {
    return this.exportPNG({ maxSize: size, padding: 10 });
  }

  // --- lifecycle ----------------------------------------------------------

  destroy() {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.detachInput();
    this.resizeObserver?.disconnect();
    setImageLoadListener(null);
    for (const canvas of [this.canvasMain, this.canvasOverlay]) canvas.remove();
  }
}

const HANDLE_CURSORS: Record<string, string> = {
  nw: 'nwse-resize',
  se: 'nwse-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  move: 'move',
};

const cursorForHandle = (handle: HandleId) => HANDLE_CURSORS[handle] ?? 'default';

function isTypingTarget(target: EventTarget | null) {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

export { clamp };
